import React, { useState, useEffect } from "react";

import Header from "./Header";
import Footer from "./Footer";
import Topsection from "./Topsection";
import FloatingButton from "./FloatingButton";
import Mobilefooterbtn from "./Mobilefooterbtn";

import { Helmet } from 'react-helmet';

/* ── Lazy YouTube Embed ───────────────────────────── */
const LazyYouTubeEmbed = ({ videoUrl }) => {
  const [isPlayerActive, setPlayerActive] = useState(false);

  const getVideoId = (url) => {
    const m = url.match(/(?:youtube\.com\/.*v=|youtu\.be\/)([^&#?/]+)/);
    return m ? m[1] : null;
  };

  const id = getVideoId(videoUrl);
  if (!id) return <p className="text-danger">Invalid YouTube link</p>;

  const thumbnail = `https://img.youtube.com/vi/${id}/hqdefault.jpg`;

  return (
    <div
      className="position-relative rounded overflow-hidden"
      style={{ cursor: "pointer", height: "300px" }}
      onClick={() => setPlayerActive(true)}
    >
      {isPlayerActive ? (
        <iframe
          src={`https://www.youtube.com/embed/${id}?autoplay=1`}
          title="YouTube player"
          width="100%"
          height="300px"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <>
          <img
            src={thumbnail}
            alt="Video Thumbnail"
            className="w-100 h-100 object-fit-cover"
            loading="lazy"
          />
          <div
            className="position-absolute top-50 start-50 translate-middle text-white"
            style={{ fontSize: "2rem" }}
          >
            ▶️
          </div>
        </>
      )}
    </div>
  );
};

/* ── Success Stories main component ───────────────── */
const PER_PAGE = 12;

const SuccessStories = () => {
  const [selectedCategory, setSelectedCategory] = useState("image");
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  /* pagination state */
  const [visibleImages, setVisibleImages] = useState(PER_PAGE);
  const [visibleVideos, setVisibleVideos] = useState(PER_PAGE);

  /* fetch once */
  useEffect(() => {
    const fetchStories = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE}/success`);
        const data = await res.json();
        setStories(data.reverse());       // newest first
      } catch (err) {
        console.error("Error fetching success stories:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchStories();
  }, []);

  /* derived lists */
  const imageList = stories.filter((s) => s.youtubelink === "-");
  const videoList = stories.filter((s) => s.youtubelink !== "-");

  /* handlers */
  const loadMoreImages = () => setVisibleImages((n) => n + PER_PAGE);
  const loadMoreVideos = () => setVisibleVideos((n) => n + PER_PAGE);

  /* reset counts when tab changes */
  useEffect(() => {
    setVisibleImages(PER_PAGE);
    setVisibleVideos(PER_PAGE);
  }, [selectedCategory]);

  if (loading) return <p className="text-center py-5">Loading…</p>;

  return (
    <div>
      <Helmet>
                  <title>Fortune Cloud Technologies | Success Stories</title>
                  <meta name="description" content="Explore courses and training programs at Fortune Cloud Technologies. Learn with experts and get placed." />
                  <meta name="robots" content="index, follow" />
          </Helmet>
      <Topsection />
      <Header />

      <div className="courseslinear">
        <div className="container">
          <div className="row">
            <div className="col-12 p-2">
              <h2 className="myfont1 fw-bold pt-2 text-dark">Success Stories</h2>
            </div>

            {/* ── Tabs ── */}
            <div className="col-12">
              <nav className="mb-3 myfont1 fw-bold">
                <div className="nav nav-tabs justify-content-center">
                  <button
                    className={`nav-link ${selectedCategory === "image" ? "active" : ""}`}
                    onClick={() => setSelectedCategory("image")}
                  >
                    Images
                  </button>
                  <button
                    className={`nav-link ${selectedCategory === "video" ? "active" : ""}`}
                    onClick={() => setSelectedCategory("video")}
                  >
                    Videos
                  </button>
                </div>
              </nav>

              {/* ── Images Tab ── */}
              {selectedCategory === "image" && (
                <>
                  <div className="row">
                    {imageList.slice(0, visibleImages).map((s, i) => (
                      <div className="col-md-2 col-6 mb-4" key={i}>
                        <img
                          src={`${process.env.REACT_APP_API_BASE}/uploads/successStory/${s.image}`}
                          alt={s.name || "Student image"}
                          className="img-fluid rounded-5 mb-3"
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>

                  {visibleImages < imageList.length && (
                    <div className="text-center mb-4">
                      <button className="btn btn-primary" onClick={loadMoreImages}>
                        Load More
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* ── Videos Tab ── */}
              {selectedCategory === "video" && (
                <>
                  <div className="row">
                    {videoList.slice(0, visibleVideos).map((s, i) => (
                      <div className="col-md-3 col-12 mb-4" key={i}>
                        <div className="border p-3 rounded shadow-sm bg-secondary-subtle border border-white border-5 h-100">
                          <LazyYouTubeEmbed videoUrl={s.youtubelink} />
                        </div>
                      </div>
                    ))}
                  </div>

                  {visibleVideos < videoList.length && (
                    <div className="text-center mb-4">
                      <button className="btn btn-primary" onClick={loadMoreVideos}>
                        Load More
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <br />
        </div>
      </div>

      <Footer />
      <FloatingButton />
      <Mobilefooterbtn />
    </div>
  );
};

export default SuccessStories;
